import { ip_back } from "../config";

function authHeaders() {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

async function parseJson(resp) {
  try {
    return await resp.json();
  } catch {
    return {};
  }
}

function isNetworkFetchError(err) {
  const msg = String(err?.message || err || "").toLowerCase();
  return (
    err instanceof TypeError ||
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("load failed")
  );
}

function formatFetchError(err, context) {
  if (isNetworkFetchError(err)) {
    return (
      `Lost connection to the pose server while ${context}. ` +
      "The job may still be running — wait a moment and try again, or check your network."
    );
  }
  return String(err?.message || err || "Pose request failed");
}

/**
 * Older iOS uploads zeroed "hidden" joint scores while keeping x/y. Web then
 * drew ROI/angles with an empty skeleton — revive plausible joints for display.
 */
function reviveZeroScoreJoints(result) {
  if (!result || !Array.isArray(result.samples)) return result;
  for (const sample of result.samples) {
    if (!sample || typeof sample !== "object") continue;
    for (const side of ["left", "right"]) {
      const joints = sample[side]?.joints;
      const jointsFull = sample[side]?.joints_full;
      for (const list of [joints, jointsFull]) {
        if (!Array.isArray(list)) continue;
        for (const j of list) {
          if (!j || typeof j !== "object") continue;
          const score = Number(j.score);
          if (Number.isFinite(score) && score > 0) continue;
          const x = Number(j.x);
          const y = Number(j.y);
          if (
            Number.isFinite(x) &&
            Number.isFinite(y) &&
            x > 0.005 &&
            y > 0.005 &&
            x < 0.995 &&
            y < 0.995
          ) {
            j.score = 0.35;
          }
        }
      }
    }
  }
  return result;
}

/** True when stored samples include joints for both court sides (Player A + B). */
export function poseResultHasBothPlayers(result) {
  if (!result || typeof result !== "object") return false;
  const mode = String(
    result.settings?.players_mode || result.settings?.players || ""
  )
    .trim()
    .toLowerCase();
  if (mode === "both" || mode === "ab" || mode === "a+b" || mode === "all") {
    return true;
  }
  const samples = Array.isArray(result.samples) ? result.samples : [];
  let hasLeft = false;
  let hasRight = false;
  const limit = Math.min(samples.length, 80);
  for (let i = 0; i < limit; i += 1) {
    const s = samples[i];
    if (Array.isArray(s?.left?.joints) && s.left.joints.length) hasLeft = true;
    if (Array.isArray(s?.right?.joints) && s.right.joints.length) hasRight = true;
    if (hasLeft && hasRight) return true;
  }
  return false;
}

/**
 * fetch with retries on transient network errors (browser "Failed to fetch").
 */
async function fetchJson(url, options = {}, { retries = 3, retryDelayMs = 1500 } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    if (options.signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
    try {
      const resp = await fetch(url, options);
      const json = await parseJson(resp);
      return { resp, json };
    } catch (err) {
      lastErr = err;
      if (err?.name === "AbortError") throw err;
      if (!isNetworkFetchError(err) || attempt >= retries) throw err;
      await delay(retryDelayMs * (attempt + 1), options.signal);
    }
  }
  throw lastErr;
}

function appendClipParams(q, { videoUrl, sequenceOrder } = {}) {
  if (videoUrl) q.set("video_url", String(videoUrl));
  const seq = Number(sequenceOrder);
  if (Number.isFinite(seq) && seq > 0) {
    q.set("sequence_order", String(Math.floor(seq)));
  }
  return q;
}

/**
 * Load existing complete pose result for this exercise clip (no enqueue).
 * Pass videoUrl / sequenceOrder so multi-sequence exercises do not share joints.
 * Ensures settings.pose_fps is present when the API reports it.
 */
export async function tryLoadExistingPose(teId, clip = {}) {
  if (!teId) return null;
  const q = appendClipParams(new URLSearchParams({ te_id: teId }), clip);
  const resp = await fetch(
    `${ip_back}api/mobileAPI/pose/results?${q.toString()}`,
    { method: "GET", headers: authHeaders() }
  );
  const json = await parseJson(resp);
  if (!resp.ok || !json?.data) return null;
  if (json.data.status !== "complete") return null;
  const result =
    json.data.result ||
    (json.data.result_json
      ? typeof json.data.result_json === "string"
        ? JSON.parse(json.data.result_json)
        : json.data.result_json
      : null);
  if (!result || typeof result !== "object") return null;
  reviveZeroScoreJoints(result);
  const apiFps = Number(json.data.pose_fps);
  const settingsFps = Number(result.settings?.pose_fps);
  const deviceTier = json.data.device_tier
    ? String(json.data.device_tier)
    : result.settings?.device_tier || null;
  const source =
    json.data.source ||
    result.settings?.source ||
    (deviceTier && /ios/i.test(String(deviceTier)) ? "ios_on_device" : null);
  let next = result;
  if (
    (!Number.isFinite(settingsFps) || settingsFps <= 0) &&
    Number.isFinite(apiFps) &&
    apiFps > 0
  ) {
    next = {
      ...result,
      settings: { ...(result.settings || {}), pose_fps: apiFps },
    };
  }
  if (source || deviceTier) {
    next = {
      ...next,
      settings: {
        ...(next.settings || {}),
        ...(source ? { source } : null),
        ...(deviceTier ? { device_tier: deviceTier } : null),
      },
    };
  }
  return next;
}

/**
 * Enqueue (or reuse) pose job and poll until complete.
 * Optional startFrame / endFrame (-1 = through end) limit processing range.
 * roiLeft / roiRight (normalized {x,y,w,h}) crop pose to player boxes.
 * onProgress({ progress, etaSeconds, status })
 */
export async function fetchOrEnqueuePoseResult(
  teId,
  {
    poseFps = 30,
    frameStride = null,
    startFrame = 0,
    endFrame = -1,
    swapPlayers = false,
    players = "A",
    roiLeft = null,
    roiRight = null,
    videoUrl = null,
    rotationDeg = 0,
    flipH = false,
    flipV = false,
    viewSetup = null,
    pose3dEnabled = false,
    pose3dMode = "mono_world",
    pose3dPlayer = null,
    cameraGroupId = null,
    pose3dPeers = null,
    forceServer = false,
    sequenceOrder = null,
    onProgress,
    signal,
    maxAttempts = 600,
  } = {}
) {
  if (!teId) return null;

  const sf0 = Math.max(0, Number.isFinite(startFrame) ? Math.floor(startFrame) : 0);
  let ef = Number.isFinite(endFrame) ? Math.floor(endFrame) : -1;
  let sf = sf0;
  if (ef >= 0 && ef < sf) {
    const tmp = sf;
    sf = ef;
    ef = tmp;
  }

  const body = {
    te_id: teId,
    start_frame: sf,
    end_frame: ef,
    swap_players: swapPlayers ? 1 : 0,
    players: players || undefined,
    roi_left: roiLeft || undefined,
    roi_right: roiRight || undefined,
    video_url: videoUrl || undefined,
    sequence_order:
      Number.isFinite(Number(sequenceOrder)) && Number(sequenceOrder) > 0
        ? Math.floor(Number(sequenceOrder))
        : undefined,
  };
  if (Number.isFinite(poseFps) && poseFps > 0) {
    body.pose_fps = poseFps;
  } else if (Number.isFinite(frameStride) && frameStride >= 1) {
    body.frame_stride = frameStride;
  } else {
    body.pose_fps = 30;
  }
  if (viewSetup) {
    body.view_setup = viewSetup;
  } else {
    if (rotationDeg) body.rotation_deg = rotationDeg;
    if (flipH) body.flip_h = true;
    if (flipV) body.flip_v = true;
  }
  if (pose3dEnabled) {
    body.pose3d_enabled = true;
    body.pose3d_mode = pose3dMode || "mono_world";
    if (pose3dPlayer) body.pose3d_player = pose3dPlayer;
    if (cameraGroupId) body.camera_group_id = cameraGroupId;
    if (Array.isArray(pose3dPeers) && pose3dPeers.length) {
      body.pose3d_peers = pose3dPeers;
    }
  }
  if (forceServer) {
    body.force_server = 1;
  }

  let enqueueJson;
  try {
    const enqueued = await fetchJson(
      `${ip_back}api/mobileAPI/pose/jobs`,
      {
        method: "POST",
        headers: authHeaders(),
        signal,
        body: JSON.stringify(body),
      },
      { retries: 3 }
    );
    enqueueJson = enqueued.json;
    if (!enqueued.resp.ok) {
      throw new Error(
        enqueueJson?.message || `Could not start pose job (${enqueued.resp.status})`
      );
    }
  } catch (err) {
    throw new Error(formatFetchError(err, "starting analysis"));
  }
  const jobId = enqueueJson?.data?.job_id || null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const q = appendClipParams(new URLSearchParams({ te_id: teId }), {
      videoUrl,
      sequenceOrder,
    });
    if (jobId) q.set("job_id", jobId);
    let resp;
    let json;
    try {
      const polled = await fetchJson(
        `${ip_back}api/mobileAPI/pose/results?${q.toString()}`,
        { method: "GET", headers: authHeaders(), signal },
        { retries: 2 }
      );
      resp = polled.resp;
      json = polled.json;
    } catch (err) {
      throw new Error(formatFetchError(err, "checking progress"));
    }
    const data = json?.data;
    if (!data) {
      await delay(2000, signal);
      continue;
    }

    const progress = Number(data.progress) || 0;
    const etaSeconds =
      data.eta_seconds != null ? Number(data.eta_seconds) : null;
    onProgress?.({
      progress,
      etaSeconds: Number.isFinite(etaSeconds) ? etaSeconds : null,
      status: data.status,
    });

    if (data.status === "complete") {
      const result =
        data.result ||
        (data.result_json
          ? typeof data.result_json === "string"
            ? JSON.parse(data.result_json)
            : data.result_json
          : null);
      if (!result || typeof result !== "object") return result;
      reviveZeroScoreJoints(result);
      const deviceTier = data.device_tier
        ? String(data.device_tier)
        : result.settings?.device_tier || null;
      const source =
        data.source ||
        result.settings?.source ||
        (deviceTier && /ios/i.test(String(deviceTier))
          ? "ios_on_device"
          : "server");
      return {
        ...result,
        job_id: data.job_id || jobId || result.video_hash || null,
        settings: {
          ...(result.settings || {}),
          source,
          ...(deviceTier ? { device_tier: deviceTier } : null),
        },
      };
    }
    if (data.status === "failed") {
      throw new Error(data.error_message || "Pose analysis failed");
    }
    await delay(attempt === 0 ? 500 : 2000, signal);
  }
  throw new Error(
    "Pose analysis timed out while waiting for the server. " +
      "Try a shorter frame range (In/Out) or run again later."
  );
}

/**
 * Full-frame multi-person detect on one video frame (no ROI).
 * Returns suggested_left / suggested_right: on each half, the pose with the
 * largest shoulder→hip distance (poses missing shoulder or hip are skipped).
 * Prefer frameJpeg so detection matches the paused on-screen pixels.
 */
export async function detectPoseCalibrationFrame(
  teId,
  timeMs = 0,
  signal,
  frameJpeg = null,
  videoSize = null,
  videoUrl = null,
  viewSetup = null,
  tableCorners = null
) {
  if (!teId) throw new Error("te_id required");
  const body = {
    te_id: teId,
    time_ms: Math.max(0, Math.round(timeMs || 0)),
  };
  if (typeof frameJpeg === "string" && frameJpeg.startsWith("data:image")) {
    body.frame_jpeg = frameJpeg;
  }
  if (viewSetup) body.view_setup = viewSetup;
  if (Array.isArray(tableCorners) && tableCorners.length === 4) {
    body.table_corners = tableCorners;
  }
  const vw = Number(videoSize?.w || videoSize?.width || 0);
  const vh = Number(videoSize?.h || videoSize?.height || 0);
  if (vw > 0 && vh > 0) {
    body.video_width = Math.round(vw);
    body.video_height = Math.round(vh);
  }
  if (videoUrl && /^https?:\/\//i.test(String(videoUrl))) {
    body.video_url = String(videoUrl);
  }
  let resp;
  try {
    resp = await fetch(`${ip_back}api/mobileAPI/pose/calibrate-frame`, {
      method: "POST",
      headers: authHeaders(),
      signal,
      body: JSON.stringify(body),
    });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    throw new Error(formatFetchError(err, "detecting this frame"));
  }
  const json = await parseJson(resp);
  if (!resp.ok || json?.status === false) {
    throw new Error(json?.message || "Player detection failed");
  }
  return json.data;
}

/**
 * Validate that a player pose exists inside a coach-drawn ROI on the current frame.
 * Prefer frameJpeg (data URL from <video> canvas capture) so detection matches
 * the exact paused pixels — OpenCV seek often drifts / differs in orientation.
 * Pass videoWidth/Height so the worker can rotate storage frames to match the browser.
 * Returns { found, player: { joints }, message, ... }.
 */
export async function detectPoseInRoi(
  teId,
  timeMs,
  roi,
  signal,
  frameJpeg = null,
  videoSize = null,
  videoUrl = null,
  viewSetup = null
) {
  if (!teId) throw new Error("te_id required");
  if (!roi) throw new Error("roi required");
  const body = {
    te_id: teId,
    time_ms: Math.max(0, Math.round(timeMs || 0)),
    roi,
  };
  if (typeof frameJpeg === "string" && frameJpeg.startsWith("data:image")) {
    body.frame_jpeg = frameJpeg;
  }
  if (viewSetup) body.view_setup = viewSetup;
  const vw = Number(videoSize?.w || videoSize?.width || 0);
  const vh = Number(videoSize?.h || videoSize?.height || 0);
  if (vw > 0 && vh > 0) {
    body.video_width = Math.round(vw);
    body.video_height = Math.round(vh);
  }
  if (videoUrl) body.video_url = String(videoUrl);
  const resp = await fetch(`${ip_back}api/mobileAPI/pose/calibrate-frame`, {
    method: "POST",
    headers: authHeaders(),
    signal,
    body: JSON.stringify(body),
  });
  const json = await parseJson(resp);
  if (!resp.ok || json?.status === false) {
    throw new Error(json?.message || "Player detection failed");
  }
  return json.data;
}

/**
 * Validate both player ROIs in one request — rejects same-player detections and
 * enforces table half-court split when table_corners are provided.
 */
export async function detectPoseInBothRois(
  teId,
  timeMs,
  roiLeft,
  roiRight,
  signal,
  frameJpeg = null,
  videoSize = null,
  videoUrl = null,
  viewSetup = null,
  tableCorners = null
) {
  if (!teId) throw new Error("te_id required");
  if (!roiLeft || !roiRight) throw new Error("roi_left and roi_right required");
  const body = {
    te_id: teId,
    time_ms: Math.max(0, Math.round(timeMs || 0)),
    roi_left: roiLeft,
    roi_right: roiRight,
  };
  if (Array.isArray(tableCorners) && tableCorners.length === 4) {
    body.table_corners = tableCorners;
  }
  if (typeof frameJpeg === "string" && frameJpeg.startsWith("data:image")) {
    body.frame_jpeg = frameJpeg;
  }
  if (viewSetup) body.view_setup = viewSetup;
  const vw = Number(videoSize?.w || videoSize?.width || 0);
  const vh = Number(videoSize?.h || videoSize?.height || 0);
  if (vw > 0 && vh > 0) {
    body.video_width = Math.round(vw);
    body.video_height = Math.round(vh);
  }
  if (videoUrl) body.video_url = String(videoUrl);
  const resp = await fetch(`${ip_back}api/mobileAPI/pose/calibrate-frame`, {
    method: "POST",
    headers: authHeaders(),
    signal,
    body: JSON.stringify(body),
  });
  const json = await parseJson(resp);
  if (!resp.ok || json?.status === false) {
    throw new Error(json?.message || "Player detection failed");
  }
  return json.data;
}

/** Delete cached pose jobs for this clip (or whole te_id if no clip given). */
export async function flushPoseResults(teId, clip = {}) {
  if (!teId) throw new Error("te_id required");
  const params = appendClipParams(new URLSearchParams({ te_id: teId }), clip);
  const resp = await fetch(
    `${ip_back}api/mobileAPI/pose/flush?${params.toString()}`,
    { method: "POST", headers: authHeaders() }
  );
  const json = await parseJson(resp);
  if (!resp.ok || json?.status === false) {
    throw new Error(json?.message || "Failed to clear pose results");
  }
  return json.data;
}

/**
 * Recompute serve / bounce-zone metrics after table corners change.
 * Uses stored pose joints and ball bounce xy — does not re-run tracking.
 */
export async function refreshTableDependentMetrics({
  teId,
  cornersNorm,
  geometryId = null,
  videoUrl = null,
  sequenceOrder = null,
  detector = null,
  signal,
} = {}) {
  if (!teId) throw new Error("te_id required");
  const body = {
    te_id: teId,
    corners_norm: cornersNorm,
  };
  if (geometryId) body.geometry_id = geometryId;
  if (videoUrl) body.video_url = String(videoUrl);
  const seq = Number(sequenceOrder);
  if (Number.isFinite(seq) && seq > 0) {
    body.sequence_order = Math.floor(seq);
  }
  if (detector) body.detector = String(detector);
  const resp = await fetch(
    `${ip_back}api/mobileAPI/pose/refresh-table-metrics`,
    {
      method: "POST",
      headers: authHeaders(),
      signal,
      body: JSON.stringify(body),
    }
  );
  const json = await parseJson(resp);
  if (!resp.ok || json?.status === false) {
    throw new Error(json?.message || "Failed to refresh table metrics");
  }
  return json.data;
}

/**
 * Fetch Unity-friendly pose3d timeline for a completed pose job.
 */
export async function fetchPose3dResult({
  jobId = null,
  teId = null,
  mode = null,
  videoUrl = null,
  sequenceOrder = null,
  signal,
} = {}) {
  const q = new URLSearchParams();
  if (jobId) q.set("job_id", jobId);
  if (teId) q.set("te_id", teId);
  if (mode) q.set("mode", mode);
  if (videoUrl) q.set("video_url", String(videoUrl));
  const seq = Number(sequenceOrder);
  if (Number.isFinite(seq) && seq > 0) q.set("sequence_order", String(Math.floor(seq)));
  if (!jobId && !teId) throw new Error("job_id or te_id required");
  const polled = await fetchJson(
    `${ip_back}api/mobileAPI/pose3d/results?${q.toString()}`,
    { method: "GET", headers: authHeaders(), signal },
    { retries: 2 }
  );
  if (polled.resp.status === 404) return null;
  if (!polled.resp.ok) {
    throw new Error(
      polled.json?.message || `pose3d fetch failed (${polled.resp.status})`
    );
  }
  return polled.json?.data || null;
}

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });
}
